import { useState, useEffect, useCallback } from 'react';
import {
  Car,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Shield,
  FileText,
  TrendingUp,
  Users,
  AlertCircle,
  Clock
} from 'lucide-react';
import { getVehicleStats, getVehiclesRequiringService } from '../services/vehicleService';
import { getAllVehicleChecks } from '../services/vehicleCheckService';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

const StatCard = ({ title, value, icon: Icon, color, subtitle, onClick }) => (
  <div
    className={`bg-white rounded-lg shadow-md border border-gray-200 p-6 transition-all ${onClick ? 'cursor-pointer hover:shadow-lg hover:scale-105' : ''}`}
    onClick={onClick}
  >
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
        <Icon className={`w-6 h-6 ${color}`} />
      </div>
    </div>
  </div>
);

const VehiclesDashboard = () => {
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    serviceDue: 0,
    inspectionDue: 0,
    insuranceExpiring: 0
  });
  const [vehiclesRequiringAttention, setVehiclesRequiringAttention] = useState([]);
  const [vehicleChecks, setVehicleChecks] = useState([]);
  const [driversNotSubmitted, setDriversNotSubmitted] = useState([]);
  const [vehiclesWithIssues, setVehiclesWithIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, expiring, issues, drivers

  const fetchAllData = useCallback(async () => {
    try {
      const [statsData, attentionVehicles, checksData, driversData, assignmentsData] = await Promise.all([
        getVehicleStats(),
        getVehiclesRequiringService(),
        getAllVehicleChecks(null),
        getDocs(collection(db, 'drivers')),
        getDocs(collection(db, 'vehicle_assignments'))
      ]);

      setStats(statsData);
      setVehiclesRequiringAttention(attentionVehicles);
      setVehicleChecks(checksData);

      // Find vehicles with issues (defective items)
      const vehiclesWithDefects = checksData
        .filter(check => hasDefects(check) && check.status === 'pending')
        .map(check => ({
          id: check.id,
          vehiclePlate: check.vehiclePlate,
          vehicleInfo: check.vehicleInfo,
          driverName: check.driverName,
          checkDate: check.checkDate,
          defectCount: countDefects(check)
        }));
      setVehiclesWithIssues(vehiclesWithDefects);

      // Find drivers who haven't submitted today's check
      const today = new Date().toISOString().split('T')[0];
      const todayChecks = checksData.filter(check => check.checkDate === today);
      const todayCheckDriverIds = new Set(todayChecks.map(check => check.driverId));

      // Get active assignments
      const activeAssignments = assignmentsData.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(assignment => assignment.status === 'active');

      const activeDriverIds = new Set(activeAssignments.map(a => a.driver_id));

      const driversNotSubmittedToday = driversData.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(driver => activeDriverIds.has(driver.id) && !todayCheckDriverIds.has(driver.id))
        .map(driver => {
          const assignment = activeAssignments.find(a => a.driver_id === driver.id);
          return {
            ...driver,
            assignmentId: assignment?.id,
            vehicleId: assignment?.vehicle_id
          };
        });

      setDriversNotSubmitted(driversNotSubmittedToday);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const hasDefects = (check) => {
    const allConditions = [
      check.externalBodyworkCondition,
      check.externalWiperBladesCondition,
      check.externalCleannessCondition,
      check.externalTyresCondition,
      check.interiorSeatBeltsCondition,
      check.interiorFirstAidKitCondition,
      check.interiorFireExtinguisherCondition,
      check.interiorTorchCondition,
      check.interiorWarningTriangleCondition,
      check.interiorLogBookCondition,
      check.interiorSpareTyreCondition,
      check.fluidsEngineOilCondition,
      check.fluidsCoolantCondition,
      check.fluidsWindscreenWashCondition,
      check.fluidsBrakeClutchCondition,
      check.fluidsPowerSteeringCondition,
      check.fluidsBatteryCondition,
      check.fluidsLeaksCondition,
      check.functionWarningLightCondition,
      check.functionAllLightsCondition,
      check.functionHornCondition,
      check.functionWashersWipersCondition,
      check.functionBrakeCondition,
      check.functionFuelCondition,
    ];
    return allConditions.includes('defective');
  };

  const countDefects = (check) => {
    const allConditions = [
      check.externalBodyworkCondition,
      check.externalWiperBladesCondition,
      check.externalCleannessCondition,
      check.externalTyresCondition,
      check.interiorSeatBeltsCondition,
      check.interiorFirstAidKitCondition,
      check.interiorFireExtinguisherCondition,
      check.interiorTorchCondition,
      check.interiorWarningTriangleCondition,
      check.interiorLogBookCondition,
      check.interiorSpareTyreCondition,
      check.fluidsEngineOilCondition,
      check.fluidsCoolantCondition,
      check.fluidsWindscreenWashCondition,
      check.fluidsBrakeClutchCondition,
      check.fluidsPowerSteeringCondition,
      check.fluidsBatteryCondition,
      check.fluidsLeaksCondition,
      check.functionWarningLightCondition,
      check.functionAllLightsCondition,
      check.functionHornCondition,
      check.functionWashersWipersCondition,
      check.functionBrakeCondition,
      check.functionFuelCondition,
    ];
    return allConditions.filter(c => c === 'defective').length;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const expiryDate = new Date(date);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 30 && diffDays >= 0;
  };

  const getDaysUntilExpiry = (date) => {
    if (!date) return null;
    const expiryDate = new Date(date);
    const today = new Date();
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  const pendingChecks = vehicleChecks.filter(check => check.status === 'pending');
  const approvedChecks = vehicleChecks.filter(check => check.status === 'approved');

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading dashboard...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Dashboard</h1>
        </div>
        <button
          onClick={fetchAllData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Refresh Data
        </button>
      </div>

      {/* Stats Grid - Vehicle Stats */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Fleet Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <StatCard
            title="Total Vehicles"
            value={stats.total}
            icon={Car}
            color="text-blue-600"
          />
          <StatCard
            title="Active"
            value={stats.active}
            icon={CheckCircle}
            color="text-green-600"
          />
          <StatCard
            title="Inactive"
            value={stats.inactive}
            icon={XCircle}
            color="text-red-600"
          />
          <StatCard
            title="Inspection Due"
            value={stats.inspectionDue}
            icon={FileText}
            color="text-yellow-600"
            subtitle="Within 30 days"
            onClick={() => setActiveTab('expiring')}
          />
          <StatCard
            title="Insurance Expiring"
            value={stats.insuranceExpiring}
            icon={Shield}
            color="text-orange-600"
            subtitle="Within 30 days"
            onClick={() => setActiveTab('expiring')}
          />
          <StatCard
            title="Needs Attention"
            value={vehiclesRequiringAttention.length}
            icon={AlertTriangle}
            color="text-red-600"
            subtitle="Requires action"
            onClick={() => setActiveTab('expiring')}
          />
        </div>
      </div>

      {/* Daily Check Stats */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Daily Checks Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Pending Review"
            value={pendingChecks.length}
            icon={Clock}
            color="text-yellow-600"
            subtitle="Awaiting approval"
            onClick={() => setActiveTab('overview')}
          />
          <StatCard
            title="Vehicles with Issues"
            value={vehiclesWithIssues.length}
            icon={AlertCircle}
            color="text-red-600"
            subtitle="Defects reported"
            onClick={() => setActiveTab('issues')}
          />
          <StatCard
            title="Drivers Not Submitted"
            value={driversNotSubmitted.length}
            icon={Users}
            color="text-orange-600"
            subtitle="Today's check missing"
            onClick={() => setActiveTab('drivers')}
          />
          <StatCard
            title="Approved Today"
            value={approvedChecks.filter(c => c.checkDate === new Date().toISOString().split('T')[0]).length}
            icon={CheckCircle}
            color="text-green-600"
            subtitle="All clear"
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="border-b border-gray-200">
          <div className="flex gap-1 p-2">
            {[
              { id: 'overview', label: 'Pending Checks', count: pendingChecks.length },
              { id: 'expiring', label: 'Expiring Documents', count: vehiclesRequiringAttention.length },
              { id: 'issues', label: 'Vehicles with Issues', count: vehiclesWithIssues.length },
              { id: 'drivers', label: 'Missing Submissions', count: driversNotSubmitted.length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.label} {tab.count > 0 && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                    activeTab === tab.id ? 'bg-blue-500' : 'bg-gray-200'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {/* Pending Checks Tab */}
          {activeTab === 'overview' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Vehicle Checks</h3>
              {pendingChecks.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">No pending checks to review</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Odometer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issues</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {pendingChecks.map((check) => (
                        <tr key={check.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {check.checkDate}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{check.vehiclePlate}</div>
                            <div className="text-xs text-gray-500">{check.vehicleInfo}</div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm text-gray-900">{check.driverName}</div>
                            <div className="text-xs text-gray-500">{check.driverPhone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {check.odometerReading} km
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    check.fuelLevel >= 50 ? 'bg-green-500' :
                                    check.fuelLevel >= 25 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${check.fuelLevel}%` }}
                                ></div>
                              </div>
                              <span className="text-sm text-gray-900">{check.fuelLevel}%</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {hasDefects(check) ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Has Issues
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                All Good
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Expiring Documents Tab */}
          {activeTab === 'expiring' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vehicles Requiring Attention</h3>
              {vehiclesRequiringAttention.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">All documents are up to date</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Vehicle
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Make/Model
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Inspection
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Insurance
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Priority
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vehiclesRequiringAttention.map((vehicle) => {
                        const inspectionDays = getDaysUntilExpiry(vehicle.control_technique_date);
                        const insuranceDays = getDaysUntilExpiry(vehicle.insurance_expiry_date);

                        const getInspectionStatus = () => {
                          if (isExpired(vehicle.control_technique_date)) {
                            return { text: 'EXPIRED', color: 'text-red-600 bg-red-100' };
                          } else if (isExpiringSoon(vehicle.control_technique_date)) {
                            return {
                              text: `${inspectionDays} days`,
                              color: 'text-yellow-600 bg-yellow-100'
                            };
                          }
                          return { text: 'OK', color: 'text-green-600 bg-green-100' };
                        };

                        const getInsuranceStatus = () => {
                          if (isExpired(vehicle.insurance_expiry_date)) {
                            return { text: 'EXPIRED', color: 'text-red-600 bg-red-100' };
                          } else if (isExpiringSoon(vehicle.insurance_expiry_date)) {
                            return {
                              text: `${insuranceDays} days`,
                              color: 'text-yellow-600 bg-yellow-100'
                            };
                          }
                          return { text: 'OK', color: 'text-green-600 bg-green-100' };
                        };

                        const getPriority = () => {
                          const expired = isExpired(vehicle.control_technique_date) ||
                                         isExpired(vehicle.insurance_expiry_date);
                          if (expired) {
                            return { text: 'URGENT', color: 'text-red-600 bg-red-100' };
                          }
                          return { text: 'HIGH', color: 'text-yellow-600 bg-yellow-100' };
                        };

                        const inspectionStatus = getInspectionStatus();
                        const insuranceStatus = getInsuranceStatus();
                        const priority = getPriority();

                        return (
                          <tr key={vehicle.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Car className="w-5 h-5 text-gray-400 mr-2" />
                                <span className="text-sm font-medium text-gray-900">
                                  {vehicle.registration_no}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {vehicle.make}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 mb-1">
                                  {formatDate(vehicle.control_technique_date)}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${inspectionStatus.color}`}>
                                  {inspectionStatus.text}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-sm text-gray-600 mb-1">
                                  {formatDate(vehicle.insurance_expiry_date)}
                                </span>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${insuranceStatus.color}`}>
                                  {insuranceStatus.text}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${priority.color}`}>
                                {priority.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Vehicles with Issues Tab */}
          {activeTab === 'issues' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Vehicles with Reported Issues</h3>
              {vehiclesWithIssues.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">No vehicles with issues reported</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Vehicle</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Defects</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vehiclesWithIssues.map((vehicle) => (
                        <tr key={vehicle.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vehicle.checkDate}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{vehicle.vehiclePlate}</div>
                            <div className="text-xs text-gray-500">{vehicle.vehicleInfo}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {vehicle.driverName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800">
                              {vehicle.defectCount} defect{vehicle.defectCount !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex px-2 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
                              PENDING REVIEW
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Drivers Not Submitted Tab */}
          {activeTab === 'drivers' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Drivers Missing Today's Check</h3>
              {driversNotSubmitted.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-gray-600">All drivers have submitted their daily checks</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Driver</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID Number</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {driversNotSubmitted.map((driver) => (
                        <tr key={driver.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <Users className="w-5 h-5 text-gray-400 mr-2" />
                              <span className="text-sm font-medium text-gray-900">{driver.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {driver.phone}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {driver.idNumber || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Not Submitted
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VehiclesDashboard;
